import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { Spin } from 'antd';
import { useEffect, useState } from 'react';

interface ProtectedRouteProps {
    children: React.ReactNode;
    requireAdmin?: boolean;
}

/**
 * ProtectedRoute Component
 * Protects routes that require authentication and optionally admin role
 * 
 * @param children - The component to render if authorized
 * @param requireAdmin - Whether admin role is required (default: false)
 */
export const ProtectedRoute = ({ children, requireAdmin = false }: ProtectedRouteProps) => {
    const location = useLocation();
    const { isAuthenticated, user, checkAuth } = useAuthStore();
    const [isChecking, setIsChecking] = useState(true);

    useEffect(() => {
        // Check auth on mount
        const verifyAuth = async () => {
            if (!isAuthenticated) {
                await checkAuth();
            }
            setIsChecking(false);
        };

        verifyAuth();
    }, [isAuthenticated, checkAuth]);

    // Show loading while checking authentication
    if (isChecking) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    // Not authenticated - redirect to login
    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Require admin but user is not admin - redirect to home
    if (requireAdmin && user?.role !== 'admin') {
        return <Navigate to="/" replace />;
    }

    // Authorized - render children
    return <>{children}</>;
};

export default ProtectedRoute;
